import { useContext, useEffect, useRef, useState } from 'react';
import { UseGetCostCentersList } from '../../../../common';
import { AppContext } from '../../../../common/context/';
import { Modal } from '../BaseModal';
import { AssociateMultipleCostCentersByHourRangesProps } from './AssociateMultipleCostCentersByHourRanges.Props';
import { validateDayRows } from './validations/costCenterValidations';
import { CostCenterAssignment, DayRow, Shift } from './types/CostCenterTypes';
import Select, { components } from 'react-select';
import DistributionSummary from './components/DistributionSummary';

export const AssociateMultipleCostCentersByHourRanges = ({
    onClose,
    onSubmit,
    isOpen,
    employee,
    endDate,
    startDate,
    group,
    costCenters,
    workshiftShedulingList,
}: AssociateMultipleCostCentersByHourRangesProps) => {
    const { langResources, activeSkinConfig } = useContext(AppContext);
    const { costCenterList } = UseGetCostCentersList({ group, onClose });
    const idCounterRef = useRef(0);

    const [dayRows, setDayRows] = useState<DayRow[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<string | number>(employee[0]?.id || '');

    const [filterDate, setFilterDate] = useState<string>('');
    const [filterCostCenter, setFilterCostCenter] = useState<string>('');
    const [showFilters, setShowFilters] = useState<boolean>(false);

    const [bulkModeByEmployee, setBulkModeByEmployee] = useState<{ [employeeId: string]: boolean }>({});
    const [bulkCostCenterByEmployee, setBulkCostCenterByEmployee] = useState<{ [employeeId: string]: number }>({});

    const [costCenterIdsToRemove, setCostCenterIdsToRemove] = useState<number[]>([]);

    const [bulkStartTimeByEmployee, setBulkStartTimeByEmployee] = useState<{ [employeeId: string]: string | null }>({});
    const [bulkEndTimeByEmployee, setBulkEndTimeByEmployee] = useState<{ [employeeId: string]: string | null }>({});

    const generateId = () => {
        idCounterRef.current += 1;
        return `id-${idCounterRef.current}`;
    };

    const getEmployeeShiftsForDate = (employeeId: string | number, date: string): Shift[] => {
        if (!date || !workshiftShedulingList?.schedulings) return [];

        const dateObj = new Date(date);
        const dayOfWeek = dateObj.getDay();
        const shifts: Shift[] = [];
        const dateString = date.split('T')[0];
        workshiftShedulingList.schedulings.forEach((scheduling) => {
            const schedulingDateString = scheduling.date.split('T')[0];

            if (scheduling.employeeId === employeeId && schedulingDateString === dateString) {
                const workshift = scheduling.workshift;
                if (workshift) {
                    if (workshift.isDuplex) {
                        if (workshift.firstArrival && workshift.firstDeparture) {
                            const firstStart = new Date(workshift.firstArrival);
                            const firstEnd = new Date(workshift.firstDeparture);
                            const firstStartTime = firstStart.toTimeString().slice(0, 5);
                            const firstEndTime = firstEnd.toTimeString().slice(0, 5);

                            if (firstStartTime !== '00:00' && firstEndTime !== '00:00') {
                                shifts.push({
                                    dayOfWeek,
                                    startTime: firstStartTime,
                                    endTime: firstEndTime,
                                    shiftNumber: 1,
                                });
                            }
                        }

                        if (workshift.secondArrival && workshift.secondDeparture) {
                            const secondStart = new Date(workshift.secondArrival);
                            const secondEnd = new Date(workshift.secondDeparture);
                            const secondStartTime = secondStart.toTimeString().slice(0, 5);
                            const secondEndTime = secondEnd.toTimeString().slice(0, 5);

                            if (secondStartTime !== '00:00' && secondEndTime !== '00:00') {
                                shifts.push({
                                    dayOfWeek,
                                    startTime: secondStartTime,
                                    endTime: secondEndTime,
                                    shiftNumber: 2,
                                });
                            }
                        }
                    } else {
                        if (scheduling.startTime && scheduling.endTime) {
                            const start = new Date(scheduling.startTime);
                            const end = new Date(scheduling.endTime);
                            const startTime = start.toTimeString().slice(0, 5);
                            const endTime = end.toTimeString().slice(0, 5);

                            shifts.push({
                                dayOfWeek,
                                startTime,
                                endTime,
                                shiftNumber: 1,
                            });
                        }
                    }
                }
            }
        });
        return shifts;
    };

    const generateDateRange = (start: string, end: string): string[] => {
        const dates: string[] = [];
        const startDateObj = new Date(start);
        const endDateObj = new Date(end);

        let currentDate = new Date(startDateObj);
        while (currentDate <= endDateObj) {
            dates.push(currentDate.toISOString().split('T')[0]);
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    };

    useEffect(() => {
        idCounterRef.current = 0;

        if (isOpen) {
            const dates = generateDateRange(startDate, endDate);

            const newDayRows: DayRow[] = [];

            employee.forEach((emp) => {
                dates.forEach((date) => {
                    const shifts = getEmployeeShiftsForDate(emp.id, date);

                    const existingAssignments: CostCenterAssignment[] = costCenters
                        ?.filter((cc: any) => {
                            const ccDisplayDate = cc.date?.split('T')[0] || cc.displayDate?.split('T')[0];
                            return cc.employeeId === emp.id && ccDisplayDate === date;
                        })
                        .map((cc: any) => {
                            const scheduling = workshiftShedulingList?.schedulings?.find(
                                (s: any) => s.employeeId === emp.id && s.date.split('T')[0] === date
                            );

                            return {
                                id: generateId(),
                                costCenterId: cc.costCenterId,
                                costCenterName: cc.costCenterName,
                                startTime: cc.startTime || '00:00',
                                endTime: cc.endTime || '23:59',
                                shiftNumber: cc.shiftNumber,
                                costCenterRecordId: cc.id,
                                workshiftSchedulingId: cc.workshiftSchedulingId || scheduling?.id || 0,
                            };
                        }) || [];

                    const selectedShift = shifts.length > 0 ? shifts[0] : undefined;

                    newDayRows.push({
                        id: generateId(),
                        employeeId: emp.id,
                        employeeName: emp.text,
                        date,
                        shifts,
                        assignments: existingAssignments,
                        selectedShift,
                    });
                });
            });

            setDayRows(newDayRows);
        }
    }, [isOpen, startDate, endDate, workshiftShedulingList, costCenters, employee]);

    const currentEmployeeDayRows = dayRows.filter((row) => row.employeeId === selectedEmployee);
    const validation = validateDayRows(currentEmployeeDayRows);
    const hasOverlaps = validation.overlaps.size > 0;

    const handlerSubmit = () => {

        const allValidation = validateDayRows(dayRows);

        if (allValidation.overlaps.size > 0) {
            alert(langResources['cost.center.modal'].validation.no.overlap);
            return;
        }

        let allAssignments: any[] = [];

        Object.entries(bulkModeByEmployee).forEach(([empIdKey, isBulk]) => {
            if (isBulk && bulkCostCenterByEmployee[empIdKey] !== -1) {
                const empDayRows = dayRows.filter(row => String(row.employeeId) === empIdKey);
                const firstDayRow = empDayRows[0];
                if (firstDayRow) {
                    allAssignments.push({
                        costCenterId: bulkCostCenterByEmployee[empIdKey],
                        costCenterName: costCenterList.find((cc) => cc.costCenter.id === bulkCostCenterByEmployee[empIdKey])?.costCenter.name || '',
                        date: firstDayRow.date,
                        startTime: firstDayRow.shifts[0]?.startTime || '00:00',
                        endTime: firstDayRow.shifts[0]?.endTime || '23:59',
                        employeeId: empIdKey,
                        schedulingCostCenterId: undefined,
                        workshiftSchedulingId: undefined,
                    });
                }
            }
        });

        dayRows
            .filter(row => !bulkModeByEmployee[row.employeeId])
            .forEach((dayRow) => {

                const hasMidnightShift = dayRow.shifts.some((s: any) => {
                    const [startH] = s.startTime.split(':').map(Number);
                    const [endH] = s.endTime.split(':').map(Number);
                    return endH < startH;
                });

                dayRow.assignments.forEach((assignment) => {
                    if (assignment.costCenterId !== -1) {
                        let assignmentDate = dayRow.date;
                        const [startH] = assignment.startTime.split(':').map(Number);

                        if (hasMidnightShift && startH < 12) {
                            const nextDate = new Date(dayRow.date);
                            nextDate.setDate(nextDate.getDate() + 1);
                            assignmentDate = nextDate.toISOString().split('T')[0];
                        }

                        allAssignments.push({
                            costCenterId: assignment.costCenterId,
                            costCenterName: assignment.costCenterName,
                            date: assignmentDate,
                            startTime: assignment.startTime,
                            endTime: assignment.endTime,
                            employeeId: dayRow.employeeId,
                            schedulingCostCenterId: assignment.costCenterRecordId,
                            workshiftSchedulingId: assignment.workshiftSchedulingId ??
                                (() => {
                                    const scheduling = workshiftShedulingList?.schedulings?.find(
                                        (s: any) =>
                                            s.employeeId === dayRow.employeeId &&
                                            s.date.split('T')[0] === dayRow.date
                                    );
                                    return scheduling?.id;
                                })(),
                        });
                    }
                });
            });

        if (allAssignments.length === 0) {
            alert(langResources['cost.center.modal'].validation.no.assignments);
            return;
        }

        const employeesMap = new Map<string | number, any>();

        allAssignments.forEach((assignment) => {
            const empId = assignment.employeeId;

            if (!employeesMap.has(empId)) {
                const isBulkAssignment = !!bulkModeByEmployee[empId];
                employeesMap.set(empId, {
                    employeeId: empId,
                    isBulkAssignment,
                    BulkAssignmentStartTime: isBulkAssignment ? (bulkStartTimeByEmployee[empId] ?? null) : null,
                    BulkAssignmentEndTime: isBulkAssignment ? (bulkEndTimeByEmployee[empId] ?? null) : null,
                    costCenters: [],
                });
            }

            const empData = employeesMap.get(empId);
            const { employeeId, ...costCenterData } = assignment;
            empData.costCenters.push(costCenterData);
        });

        const employees = Array.from(employeesMap.values());

        const groupedData = {
            startDate,
            endDate,
            employees,
            costCenterIdsToRemove: costCenterIdsToRemove,
        };

        onSubmit(groupedData);

        handlerClose();
    };

    const handlerClose = () => {
        setDayRows([]);
        setFilterDate('');
        setFilterCostCenter('');
        setShowFilters(false);
        setBulkModeByEmployee({});
        setCostCenterIdsToRemove([]);
        setBulkCostCenterByEmployee({});
        setBulkStartTimeByEmployee({});
        setBulkEndTimeByEmployee({});
        onClose(false);
    };

    const handleAddCostCenter = (dayRowId: string) => {
        setDayRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id === dayRowId) {
                    const selectedShift = row.selectedShift;
                    const newAssignment: CostCenterAssignment = {
                        id: generateId(),
                        costCenterId: -1,
                        costCenterName: '',
                        startTime: selectedShift ? selectedShift.startTime : '00:00',
                        endTime: selectedShift ? selectedShift.endTime : '00:00',
                        shiftNumber: selectedShift ? selectedShift.shiftNumber : undefined,
                        isNew: true,
                    };
                    return {
                        ...row,
                        assignments: [...row.assignments, newAssignment],
                    };
                }
                return row;
            })
        );
    };

    const handleCostCenterChange = (dayRowId: string, assignmentId: string, costCenterId: number) => {
        const costCenter = costCenterList.find((cc) => cc.costCenter.id === costCenterId);

        setDayRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id === dayRowId) {
                    return {
                        ...row,
                        assignments: row.assignments.map((assignment) =>
                            assignment.id === assignmentId
                                ? {
                                    ...assignment,
                                    costCenterId,
                                    costCenterName: costCenter?.costCenter.name || '',
                                }
                                : assignment
                        ),
                    };
                }
                return row;
            })
        );
    };

    const handleTimeChange = (dayRowId: string, assignmentId: string, field: 'startTime' | 'endTime', value: string) => {
        setDayRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id === dayRowId) {
                    return {
                        ...row,
                        assignments: row.assignments.map((assignment) =>
                            assignment.id === assignmentId
                                ? { ...assignment, [field]: value, shiftNumber: undefined }
                                : assignment
                        ),
                    };
                }
                return row;
            })
        );
    };

    const handleDayShiftSelection = (dayRowId: string, shift: Shift) => {
        setDayRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id === dayRowId) {
                    const newSelectedShift = row.selectedShift?.shiftNumber === shift.shiftNumber ? undefined : shift;
                    return {
                        ...row,
                        selectedShift: newSelectedShift,
                    };
                }
                return row;
            })
        );
    };

    const handleDeleteAssignment = (dayRowId: string, assignmentId: string) => {
        setDayRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id === dayRowId) {
                    const assignmentToDelete = row.assignments.find(a => a.id === assignmentId);

                    if (assignmentToDelete?.costCenterRecordId !== undefined) {
                        setCostCenterIdsToRemove(prev => [...prev, assignmentToDelete.costCenterRecordId as number]);
                    }

                    return {
                        ...row,
                        assignments: row.assignments.filter((assignment) => assignment.id !== assignmentId),
                    };
                }
                return row;
            })
        );
    };

    const calculateDuration = (startTime: string, endTime: string): string => {
        if (!startTime || !endTime) return '-';
        const [startH, startM] = startTime.split(':').map(Number);
        const [endH, endM] = endTime.split(':').map(Number);
        let totalMinutes = endH * 60 + endM - (startH * 60 + startM);
        if (totalMinutes < 0) totalMinutes += 24 * 60;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    const clearFilters = () => {
        setFilterDate('');
        setFilterCostCenter('');
    };

    const filteredDayRows = currentEmployeeDayRows.filter((row) => {
        if (filterDate && row.date !== filterDate) return false;
        if (filterCostCenter) {
            const hasMatchingCostCenter = row.assignments.some(
                (assignment) =>
                    assignment.costCenterId !== -1 &&
                    assignment.costCenterName.toLowerCase().includes(filterCostCenter.toLowerCase())
            );
            if (!hasMatchingCostCenter) return false;
        }
        return true;
    });

    const uniqueDates = Array.from(new Set(dayRows.map((r) => r.date))).sort();
    const hasActiveFilters = filterDate || filterCostCenter;

    const isBulkActive = !!bulkModeByEmployee[selectedEmployee];
    const bulkCostCenterId = bulkCostCenterByEmployee[selectedEmployee] ?? -1;

    const totalAssignments = currentEmployeeDayRows.reduce((sum, row) => sum + row.assignments.filter(a => a.costCenterId !== -1).length, 0);

    return (
        <>
            {costCenterList.length > 0 && (
                <Modal
                    isOpen={isOpen}
                    modalTitle={langResources['cost.center.modal'].title}
                    onClose={handlerClose}
                    onSubmit={handlerSubmit}
                    disableSubmit={
                        isBulkActive
                            ? (
                                bulkCostCenterId === -1 ||
                                (!!bulkStartTimeByEmployee[selectedEmployee] !== !!bulkEndTimeByEmployee[selectedEmployee])
                            )
                            : totalAssignments === 0 || hasOverlaps
                    }
                    widthCustom='1500px'
                >
                    <div className='cost-center-modal-body'>
                        <div className='cost-center-sidebar'>
                            <div className='sidebar-header'>
                                <h6 className='sidebar-header-title'>
                                    <i className='mdi mdi-account-multiple'></i> {langResources['cost.center.modal'].employees.title}
                                </h6>
                                <p className='sidebar-header-subtitle'>{langResources['cost.center.modal'].employees.subtitle}</p>
                            </div>

                            <div className='sidebar-employees-list'>
                                {employee.map((emp) => {
                                    const empRows = dayRows.filter((r) => r.employeeId === emp.id);
                                    const empAssignments = empRows.reduce((sum, row) => sum + row.assignments.filter(a => a.costCenterId !== -1).length, 0);
                                    const isSelected = selectedEmployee === emp.id;
                                    const empValidation = validateDayRows(empRows);
                                    const empHasOverlaps = empValidation.overlaps.size > 0;
                                    const empHasWarnings = empValidation.warnings.size > 0;

                                    return (
                                        <button
                                            key={emp.id}
                                            type='button'
                                            onClick={() => setSelectedEmployee(emp.id)}
                                            className={`employee-button ${isSelected ? 'employee-button-selected' : 'employee-button-unselected'}`}
                                            style={isSelected ? { backgroundColor: activeSkinConfig.principalColor } : {}}
                                        >
                                            <div className='employee-button-content'>
                                                <div>
                                                    <p className='employee-button-name'>{emp.text}</p>
                                                </div>
                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    {empHasOverlaps && (
                                                        <span title='Hay solapamientos en los rangos horarios' style={{ color: '#dc3545', fontSize: '14px' }}>
                                                            <i className='mdi mdi-alert-circle'></i>
                                                        </span>
                                                    )}
                                                    {!empHasOverlaps && empHasWarnings && (
                                                        <span title='Hay advertencias de distribución' style={{ color: '#ffc107', fontSize: '14px' }}>
                                                            <i className='mdi mdi-alert'></i>
                                                        </span>
                                                    )}
                                                    <span
                                                        className='employee-button-count'
                                                        style={{
                                                            background: isSelected ? 'rgba(255,255,255,0.2)' : '#e7e7e7'
                                                        }}
                                                    >
                                                        {empAssignments}
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className='cost-center-main-content'>
                            <div className='cost-center-header'>
                                <div className='bulk-assignment-container'>
                                    <div className='bulk-assignment-header'>
                                        <div className='bulk-mode-toggle'>
                                            <input
                                                type='checkbox'
                                                id='bulk-mode'
                                                checked={isBulkActive}
                                                onChange={(e) => {
                                                    setBulkModeByEmployee((prev) => ({ ...prev, [selectedEmployee]: e.target.checked }));
                                                    if (!e.target.checked) setBulkCostCenterByEmployee((prev) => ({ ...prev, [selectedEmployee]: -1 }));
                                                }}
                                                className='bulk-mode-checkbox'
                                                style={{ accentColor: activeSkinConfig.principalColor }}
                                            />
                                            <label htmlFor='bulk-mode' className='bulk-mode-label'>
                                                {langResources['cost.center.modal'].bulk.assignment}
                                            </label>
                                        </div>
                                        {isBulkActive && (
                                            <div className='bulk-header-section'>
                                                <div className='bulk-controls-row'>
                                                    <div className='bulk-select-wrapper'>
                                                        <label>Centro de costo</label>
                                                        <Select
                                                            value={costCenterList.find(cc => cc.costCenter.id === bulkCostCenterId) || null}
                                                            onChange={(selected) => {
                                                                if (selected) {
                                                                    setBulkCostCenterByEmployee((prev) => ({
                                                                        ...prev,
                                                                        [selectedEmployee]: selected.costCenter.id
                                                                    }));
                                                                } else {
                                                                    setBulkCostCenterByEmployee((prev) => ({
                                                                        ...prev,
                                                                        [selectedEmployee]: -1
                                                                    }));
                                                                }
                                                            }}
                                                            options={costCenterList}
                                                            getOptionLabel={(cc) => {
                                                                const desc = cc.costCenter.observations || '';
                                                                const descShort = desc.length > 40 ? desc.slice(0, 40) + '...' : desc;
                                                                return `${cc.costCenter.name}${descShort ? ' - ' + descShort : ''}`;
                                                            }}
                                                            getOptionValue={cc => String(cc.costCenter.id)}
                                                            filterOption={(option, input) => {
                                                                const label = `${option.data.costCenter.name}`.toLowerCase();
                                                                const desc = (option.data.costCenter.observations || '').toLowerCase();
                                                                return label.includes(input.toLowerCase()) || desc.includes(input.toLowerCase());
                                                            }}
                                                            components={{
                                                                Option: (props) => (
                                                                    <div title={props.data.costCenter.observations || ''}>
                                                                        <components.Option {...props} />
                                                                    </div>
                                                                )
                                                            }}
                                                            isClearable
                                                            placeholder={langResources['cost.center.modal'].bulk.select}
                                                            classNamePrefix="bulk-select"
                                                        />
                                                    </div>

                                                    <div className='bulk-time-inputs'>
                                                        <div className='bulk-time-input-group'>
                                                            <label>Hora de inicio</label>
                                                            <input
                                                                type="time"
                                                                value={bulkStartTimeByEmployee[selectedEmployee] ?? ''}
                                                                onChange={e =>
                                                                    setBulkStartTimeByEmployee(prev => ({
                                                                        ...prev,
                                                                        [selectedEmployee]: e.target.value === '' ? null : e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </div>
                                                        <div className='bulk-time-input-group'>
                                                            <label>Hora de fin</label>
                                                            <input
                                                                type="time"
                                                                value={bulkEndTimeByEmployee[selectedEmployee] ?? ''}
                                                                onChange={e =>
                                                                    setBulkEndTimeByEmployee(prev => ({
                                                                        ...prev,
                                                                        [selectedEmployee]: e.target.value === '' ? null : e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {isBulkActive && bulkCostCenterId !== -1 && (
                                        <div style={{ marginTop: 8, marginBottom: 0 }}>
                                            {bulkStartTimeByEmployee[selectedEmployee] || bulkEndTimeByEmployee[selectedEmployee] ? (
                                                <span style={{ color: '#007bff', fontWeight: 600, fontSize: 13 }}>
                                                    <i className="mdi mdi-clock-outline" /> Asignación masiva de centro de costo <b>con rango horario</b>
                                                </span>
                                            ) : (
                                                <span style={{ color: '#28a745', fontWeight: 600, fontSize: 13 }}>
                                                    <i className="mdi mdi-calendar-check" /> Asignación masiva de <b>solo centro de costo</b> (todo el turno)
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!isBulkActive && (
                                    <div className='filters-container'>
                                        <div className='filters-header'>
                                            <h6 className='filters-header-title'>
                                                <i className='mdi mdi-filter-variant'></i> {langResources['cost.center.modal'].filters.title}
                                                {hasActiveFilters && <span className='filters-active-badge' style={{ backgroundColor: activeSkinConfig.principalColor }}>{langResources['cost.center.modal'].filters.active}</span>}
                                            </h6>
                                            <div className='filters-buttons'>
                                                {hasActiveFilters && (
                                                    <button
                                                        onClick={clearFilters}
                                                        className='filter-button'
                                                    >
                                                        <i className='mdi mdi-close'></i> {langResources['cost.center.modal'].filters.clear}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setShowFilters(!showFilters)}
                                                    className='filter-button'
                                                >
                                                    {showFilters ? langResources['cost.center.modal'].filters.hide : langResources['cost.center.modal'].filters.show}
                                                </button>
                                            </div>
                                        </div>

                                        {showFilters && (
                                            <div className='filters-content'>
                                                <div className='filter-field'>
                                                    <label className='filter-label'>{langResources['cost.center.modal'].filters.date.label}</label>
                                                    <select
                                                        value={filterDate}
                                                        onChange={(e) => setFilterDate(e.target.value)}
                                                        className='filter-select'
                                                    >
                                                        <option value=''>{langResources['cost.center.modal'].filters.date.all}</option>
                                                        {uniqueDates.map((date) => (
                                                            <option key={date} value={date}>
                                                                {date}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className='filter-field'>
                                                    <label className='filter-label'>{langResources['cost.center.modal'].filters.cost.center}</label>
                                                    <input
                                                        type='text'
                                                        placeholder={langResources['cost.center.modal'].filters.cost.search}
                                                        value={filterCostCenter}
                                                        onChange={(e) => setFilterCostCenter(e.target.value)}
                                                        className='filter-input'
                                                        onFocus={(e) => (e.currentTarget.style.borderColor = activeSkinConfig.principalColor)}
                                                        onBlur={(e) => (e.currentTarget.style.borderColor = '#ccc')}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {!isBulkActive && (
                                <div className='table-container'>
                                    <div className='table-info-box'>
                                        {langResources['cost.center.modal'].info.box}
                                    </div>

                                    <div className='table-wrapper'>
                                        <table className='cost-center-table'>
                                            <thead>
                                                <tr>
                                                    <th className='table-header-date'>{langResources['cost.center.modal'].table.header.date}</th>
                                                    <th className='table-header-shifts'>{langResources['cost.center.modal'].table.header.shifts}</th>
                                                    <th className='table-header-distribution'>{langResources['cost.center.modal'].table.header.distribution}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredDayRows.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className='empty-state'>
                                                            {langResources['cost.center.modal'].table.empty}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredDayRows.map((dayRow) => {
                                                        const hasValidAssignments = dayRow.assignments.some(a => a.costCenterId !== -1);
                                                        const rowOverlaps = validation.overlaps.get(dayRow.id);
                                                        const rowWarnings = validation.warnings.get(dayRow.id) ?? [];
                                                        const rowHasOverlap = !!rowOverlaps && rowOverlaps.size > 0;

                                                        return (
                                                            <tr
                                                                key={dayRow.id}
                                                                className={hasValidAssignments ? 'table-row-with-assignments' : ''}
                                                                style={rowHasOverlap ? { backgroundColor: '#fff8f8' } : {}}
                                                            >
                                                                <td className='table-cell-date'>
                                                                    {new Date(dayRow.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' })}
                                                                    {rowHasOverlap && (
                                                                        <div style={{ color: '#dc3545', fontSize: '11px', fontWeight: 600, marginTop: '4px' }}>
                                                                            <i className='mdi mdi-alert-circle'></i> {langResources['cost.center.modal'].table.overlap}
                                                                        </div>
                                                                    )}
                                                                    {!rowHasOverlap && rowWarnings.length > 0 && (
                                                                        <div style={{ color: '#856404', fontSize: '11px', fontWeight: 600, marginTop: '4px' }}>
                                                                            <i className='mdi mdi-alert'></i> {langResources['cost.center.modal'].table.warning}
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                <td className='table-cell-shifts'>
                                                                    {dayRow.shifts.length === 0 ? (
                                                                        <span className='no-shift-text'>{langResources['cost.center.modal'].table.no.shift}</span>
                                                                    ) : (
                                                                        <div className='shifts-list'>
                                                                            {dayRow.shifts.map((shift) => {
                                                                                const isSelected = dayRow.selectedShift?.shiftNumber === shift.shiftNumber;
                                                                                return (
                                                                                    <button
                                                                                        key={shift.shiftNumber}
                                                                                        type='button'
                                                                                        onClick={() => handleDayShiftSelection(dayRow.id, shift)}
                                                                                        className={`shift-button ${isSelected ? 'shift-button-selected' : 'shift-button-unselected'}`}
                                                                                        style={{
                                                                                            borderColor: isSelected ? 'none' : activeSkinConfig.principalColor,
                                                                                            backgroundColor: isSelected ? activeSkinConfig.principalColor : 'transparent',
                                                                                            color: isSelected ? 'white' : activeSkinConfig.principalColor,
                                                                                            boxShadow: isSelected ? `0 2px 8px ${activeSkinConfig.principalColor}40` : 'none',
                                                                                        }}
                                                                                        onMouseEnter={(e) => {
                                                                                            if (!isSelected) {
                                                                                                e.currentTarget.style.backgroundColor = `${activeSkinConfig.principalColor}15`;
                                                                                            }
                                                                                        }}
                                                                                        onMouseLeave={(e) => {
                                                                                            if (!isSelected) {
                                                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <div className='shift-button-content'>
                                                                                            {isSelected && <i className='mdi mdi-check-circle' style={{ fontSize: '14px' }}></i>}
                                                                                            <div>
                                                                                                <div className='shift-button-label'>{langResources['cost.center.modal'].shift.button.replace('{shiftNumber}', shift.shiftNumber.toString())}</div>
                                                                                                <div className='shift-button-time'>
                                                                                                    {langResources['cost.center.modal'].shift.time.replace('{startTime}', shift.startTime).replace('{endTime}', shift.endTime)}
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                <td className='table-cell-distribution'>
                                                                    {rowWarnings.length > 0 && (
                                                                        <div style={{
                                                                            background: '#fffbea',
                                                                            border: '1px solid #ffc107',
                                                                            borderRadius: '5px',
                                                                            padding: '6px 10px',
                                                                            marginBottom: '8px',
                                                                            fontSize: '11px',
                                                                            color: '#856404',
                                                                        }}>
                                                                            {rowWarnings.map((w, i) => (
                                                                                <div key={i}><i className='mdi mdi-alert'></i> {w}</div>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    <div className='assignments-list'>
                                                                        {[
                                                                            ...dayRow.assignments
                                                                                .filter(a => !a.isNew)
                                                                                .sort((a, b) => a.startTime.localeCompare(b.startTime)),
                                                                            ...dayRow.assignments.filter(a => a.isNew)
                                                                        ].map((assignment) => {
                                                                            const isOverlapping = !!rowOverlaps?.has(assignment.id);
                                                                            return (
                                                                                <div
                                                                                    key={assignment.id}
                                                                                    className={`assignment-row ${assignment.costCenterId === -1 ? 'assignment-row-empty' : ''}`}
                                                                                    style={{
                                                                                        borderColor: isOverlapping
                                                                                            ? '#dc3545'
                                                                                            : assignment.costCenterId === -1
                                                                                                ? '#ccc'
                                                                                                : `${activeSkinConfig.principalColor}30`,
                                                                                        backgroundColor: isOverlapping ? '#fff3f3' : undefined,
                                                                                    }}
                                                                                >
                                                                                    <Select
                                                                                        value={costCenterList.find(cc => cc.costCenter.id === assignment.costCenterId) || null}
                                                                                        onChange={(selected) => {
                                                                                            if (selected) {
                                                                                                handleCostCenterChange(dayRow.id, assignment.id, selected.costCenter.id);
                                                                                            }
                                                                                        }}
                                                                                        options={costCenterList}
                                                                                        getOptionLabel={(cc) => {
                                                                                            const desc = cc.costCenter.observations || '';
                                                                                            const descShort = desc.length > 40 ? desc.slice(0, 40) + '...' : desc;
                                                                                            return `${cc.costCenter.name}${descShort ? ' - ' + descShort : ''}`;
                                                                                        }}
                                                                                        getOptionValue={cc => String(cc.costCenter.id)}
                                                                                        filterOption={(option, input) => {
                                                                                            const label = `${option.data.costCenter.name}`.toLowerCase();
                                                                                            const desc = (option.data.costCenter.observations || '').toLowerCase();
                                                                                            return label.includes(input.toLowerCase()) || desc.includes(input.toLowerCase());
                                                                                        }}
                                                                                        components={{
                                                                                            Option: (props) => (
                                                                                                <div title={props.data.costCenter.observations || ''}>
                                                                                                    <components.Option {...props} />
                                                                                                </div>
                                                                                            )
                                                                                        }}
                                                                                        isDisabled={!!assignment.costCenterRecordId}
                                                                                        placeholder={langResources['cost.center.modal'].assignment.select}
                                                                                        classNamePrefix="assignment-select"
                                                                                        menuPortalTarget={document.body}
                                                                                        menuPlacement="auto"
                                                                                        styles={{
                                                                                            menuPortal: (base) => ({
                                                                                                ...base,
                                                                                                zIndex: 99999
                                                                                            })
                                                                                        }}
                                                                                    />

                                                                                    <input
                                                                                        type='time'
                                                                                        value={assignment.startTime}
                                                                                        onChange={(e) => handleTimeChange(dayRow.id, assignment.id, 'startTime', e.target.value)}
                                                                                        disabled={assignment.costCenterId === -1}
                                                                                        className={`assignment-input ${assignment.costCenterId === -1 ? 'assignment-input-disabled' : ''}`}
                                                                                        style={isOverlapping ? { borderColor: '#dc3545' } : {}}
                                                                                    />

                                                                                    <input
                                                                                        type='time'
                                                                                        value={assignment.endTime}
                                                                                        onChange={(e) => handleTimeChange(dayRow.id, assignment.id, 'endTime', e.target.value)}
                                                                                        disabled={assignment.costCenterId === -1}
                                                                                        className={`assignment-input ${assignment.costCenterId === -1 ? 'assignment-input-disabled' : ''}`}
                                                                                        style={isOverlapping ? { borderColor: '#dc3545' } : {}}
                                                                                    />

                                                                                    <div className='assignment-duration'>
                                                                                        <span className='assignment-duration-text'>
                                                                                            {calculateDuration(assignment.startTime, assignment.endTime)}
                                                                                        </span>
                                                                                        {assignment.shiftNumber && (
                                                                                            <span
                                                                                                className='assignment-shift-number'
                                                                                                style={{ color: activeSkinConfig.principalColor }}
                                                                                            >
                                                                                                T{assignment.shiftNumber}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>

                                                                                    <button
                                                                                        onClick={() => handleDeleteAssignment(dayRow.id, assignment.id)}
                                                                                        className='assignment-delete-button'
                                                                                    >
                                                                                        <i className='mdi mdi-delete'></i>
                                                                                    </button>
                                                                                </div>
                                                                            );
                                                                        })}

                                                                        <button
                                                                            onClick={() => handleAddCostCenter(dayRow.id)}
                                                                            className='add-assignment-button'
                                                                            style={{
                                                                                borderColor: activeSkinConfig.principalColor,
                                                                                color: activeSkinConfig.principalColor,
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.backgroundColor = `${activeSkinConfig.principalColor}15`;
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                                            }}
                                                                        >
                                                                            <i className='mdi mdi-plus-circle-outline'></i>
                                                                            {langResources['cost.center.modal'].assignment.add.title}
                                                                            {dayRow.selectedShift && (
                                                                                <span
                                                                                    className='add-assignment-shift-badge'
                                                                                    style={{
                                                                                        background: `${activeSkinConfig.principalColor}20`,
                                                                                        color: activeSkinConfig.principalColor
                                                                                    }}
                                                                                >
                                                                                    {langResources['cost.center.modal'].assignment.add.shift.replace('{shiftNumber}', dayRow.selectedShift.shiftNumber.toString()).replace('{startTime}', dayRow.selectedShift.startTime).replace('{endTime}', dayRow.selectedShift.endTime)}
                                                                                </span>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {isBulkActive && (
                                <>
                                    <DistributionSummary rows={currentEmployeeDayRows}/>
                                </>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};